import React from 'react'
import { XMarkIcon, CheckCircleIcon } from '@heroicons/react/24/outline'
import { Button } from '../../../components/ui/button'
import { evaluateEcddFormGates, isForeignPep } from './EcddStructuredForm'

const TRIGGER_LABELS = {
  alert: 'Rule / alert',
  pep: 'PEP / screening hit',
  large_payout: 'Large payout',
  risk: 'High risk rating',
  manual: 'Manual observation',
  postsmr: 'Post-SMR',
  unusual: 'Unusual / complex activity',
}

const SOF_RESP = { coop: 'Cooperative', partial: 'Partial', refused: 'Refused / evasive' }
const SOF_ASSESS = { sat: 'Satisfactory', unsat: 'Unsatisfactory', further: 'Requires further enquiry' }
const SCR_DISP = { false: 'False positive', true: 'True positive' }
const PEP_CLASS = {
  domestic: 'Domestic',
  foreign: 'Foreign',
  intorg: 'International organisation',
  notpep: 'Not a PEP (other list)',
}
const DECISION_LABELS = {
  satisfactory: 'Close — Satisfactory',
  enhanced_monitoring: 'Enhanced Monitoring',
  escalated_smr: 'Escalate → SMR',
}

const INDICATOR_LABELS = {
  structuring: 'Structuring below thresholds',
  'minimal-play': 'Minimal play / cash-through',
  'sof-mismatch': 'Activity inconsistent with profile',
  refusal: 'Reluctance / refusal to provide information',
  thirdparty: 'Third-party involvement',
  'staff-influence': 'Attempts to influence staff',
  'no-indicators': 'No indicators',
}

function dash(v) {
  if (v == null || String(v).trim() === '') return '—'
  if (v === true) return 'Yes'
  if (v === false) return 'No'
  return String(v)
}

function yesNo(v) {
  if (v === 'yes') return 'Yes'
  if (v === 'no') return 'No'
  return dash(v)
}

function Row({ label, value }) {
  if (value == null || value === '' || value === '—') return null
  return (
    <div className="grid grid-cols-[140px_1fr] sm:grid-cols-[180px_1fr] gap-2 py-1.5 border-b border-slate-100 last:border-0">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 pt-0.5">{label}</div>
      <div className="text-sm text-slate-800 whitespace-pre-wrap break-words">{value}</div>
    </div>
  )
}

function Section({ number, title, ok, children }) {
  return (
    <section className="mb-5 last:mb-0">
      <div className="flex items-center gap-2 mb-2">
        <div className="w-6 h-6 rounded-full bg-psg-teal text-white text-[11px] font-bold flex items-center justify-center shrink-0">
          {number}
        </div>
        <h3 className="text-sm font-bold text-[#073B4D] m-0 flex-1">{title}</h3>
        {ok != null && (
          <span
            className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${
              ok ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'
            }`}
          >
            {ok ? 'Complete' : 'Incomplete'}
          </span>
        )}
      </div>
      <div className="rounded-lg border border-slate-200 bg-slate-50/50 px-3.5 py-2 sm:ml-8">{children}</div>
    </section>
  )
}

/**
 * Read-only ECDD preview — selections and answers only (not live form fields).
 */
export default function EcddDocumentPreviewModal({
  open,
  onClose,
  record,
  formAnswers: a,
  decision,
  decisionRationale,
  gates,
}) {
  if (!open) return null
  const evalGates = gates || evaluateEcddFormGates(a || {})
  const gateMap = Object.fromEntries((evalGates.gates || []).map((g) => [g.id, g.ok]))
  const sofReq = isForeignPep(a) ? 'yes' : a?.sofRequired
  const indicators = Array.isArray(a?.indicators) ? a.indicators : []
  const evidenceItems = Array.isArray(a?.sofEvidenceItems) ? a.sofEvidenceItems : []
  const findings = Array.isArray(a?.sofFindings) ? a.sofFindings : []
  const sofDocs = Array.isArray(a?.sofDocs) ? a.sofDocs : []
  const actDocs = Array.isArray(a?.actDocs) ? a.actDocs : []
  const docName = (d) => (typeof d === 'string' ? d : d?.file_name || 'file')

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6">
      <div className="absolute inset-0 bg-black/45" onClick={onClose} role="presentation" />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col border border-slate-200">
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-slate-100 shrink-0">
          <div className="min-w-0">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">ECDD preview</div>
            <h2 className="text-lg font-bold text-[#073B4D] m-0 mt-0.5 truncate">
              {record?.name || 'Patron'} · {record?.id || '—'}
            </h2>
            <p className="text-xs text-slate-500 m-0 mt-1">
              Read-only summary of recorded selections — not the working form.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 shrink-0"
            aria-label="Close preview"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          <Section number={1} title="Case">
            <Row label="ECDD ID" value={record?.id} />
            <Row label="Status" value={record?.status} />
            <Row label="Risk" value={record?.risk != null ? String(record.risk) : null} />
            <Row label="Assigned" value={record?.assignedTo} />
            <Row label="Venue" value={record?.venue || record?.venueName} />
          </Section>

          <Section number={2} title="Trigger" ok={gateMap.trigger}>
            <Row label="Type" value={TRIGGER_LABELS[a?.triggerType] || a?.triggerType} />
            <Row label="Date" value={a?.triggerDate} />
            <Row label="Description" value={a?.triggerDesc} />
            <Row label="Linked alert" value={a?.linkedAlertId} />
            <Row label="Observer" value={a?.observer} />
            <Row label="Observation date" value={a?.obsDate} />
            <Row label="Prior SMR" value={a?.priorSmr} />
          </Section>

          <Section number={3} title="KYC / identity" ok={gateMap.kyc}>
            <Row label="KYC current" value={yesNo(a?.kycCurrent)} />
            {a?.kycCurrent === 'no' && (
              <>
                <Row label="Document" value={[a?.docType, a?.docNum].filter(Boolean).join(' · ')} />
                <Row label="Expiry" value={a?.docExp} />
                <Row label="Method" value={a?.verMethod} />
                <Row label="Result" value={a?.verResult} />
                <Row label="Verified" value={a?.verDate} />
              </>
            )}
            <Row label="Occupation" value={a?.occupation} />
            <Row label="Activity consistent" value={yesNo(a?.occConsistent)} />
            <Row label="Customer type" value={a?.customerType} />
          </Section>

          <Section number={4} title="Source of funds / wealth" ok={gateMap.sof}>
            <Row label="Enquiry required" value={yesNo(sofReq)} />
            {sofReq === 'no' && <Row label="Reason not required" value={a?.sofNoReason} />}
            {sofReq === 'yes' && (
              <>
                <Row label="Method" value={a?.sofMethod} />
                <Row label="Date" value={a?.sofDate} />
                <Row label="By" value={a?.sofBy} />
                <Row label="Source of funds" value={a?.sofCat === 'other' ? `Other — ${a?.sofOtherDetail || ''}` : a?.sofCat} />
                <Row label="Source of wealth" value={a?.sowCat} />
                <Row label="Evidence summary" value={a?.sofEvidence} />
                {evidenceItems.length > 0 && (
                  <Row
                    label="Evidence notes"
                    value={evidenceItems.map((e) => `• ${e.text}`).join('\n')}
                  />
                )}
                {findings.length > 0 && (
                  <Row
                    label="Findings"
                    value={findings.map((f) => `• [${f.source}] ${f.summary}`).join('\n')}
                  />
                )}
                <Row label="Patron response" value={SOF_RESP[a?.sofResp] || a?.sofResp} />
                <Row label="CO assessment" value={SOF_ASSESS[a?.sofAssess] || a?.sofAssess} />
                <Row label="Override" value={a?.satOverride} />
                {sofDocs.length > 0 && (
                  <Row label="Documents" value={sofDocs.map(docName).join('\n')} />
                )}
              </>
            )}
          </Section>

          <Section number={5} title="Activity analysis" ok={gateMap.activity}>
            {a?.noActivityToLink ? (
              <Row label="Activity" value="No activity to link" />
            ) : (
              <>
                <Row label="Period" value={[a?.actFrom, a?.actTo].filter(Boolean).join(' → ')} />
                <Row label="Summary" value={a?.actSummary} />
                <Row label="Consistent with SoF" value={yesNo(a?.actConsistent)} />
                <Row label="Inconsistency notes" value={a?.inconNotes} />
                {indicators.length > 0 && (
                  <Row
                    label="Indicators"
                    value={indicators.map((i) => INDICATOR_LABELS[i] || i).join('; ')}
                  />
                )}
                {a?.noIndicators && !indicators.some((i) => i !== 'no-indicators') && (
                  <Row label="Indicators" value="No indicators" />
                )}
                {actDocs.length > 0 && (
                  <Row label="Documents" value={actDocs.map(docName).join('\n')} />
                )}
              </>
            )}
          </Section>

          <Section number={6} title="Patron engagement" ok={gateMap.engage}>
            <Row label="Engaged" value={yesNo(a?.engaged)} />
            {a?.engaged === 'yes' && (
              <>
                <Row label="Date" value={a?.engDate} />
                <Row label="By" value={a?.engBy} />
                <Row label="Mode" value={a?.engMode} />
                <Row label="Tipping-off declared" value={a?.tipDecl ? 'Yes' : 'No'} />
              </>
            )}
            {a?.tipDeclPostSmr && <Row label="Post-SMR tipping-off" value="Declared" />}
          </Section>

          <Section number={7} title="Screening refresh" ok={gateMap.screen}>
            <Row label="Re-screen date" value={a?.scrDate} />
            <Row label="Result" value={a?.scrResult === 'clear' ? 'Clear' : a?.scrResult === 'hit' ? 'Hit' : a?.scrResult} />
            <Row label="Linked check" value={a?.scrRef ? `SCR-${a.scrRef}` : null} />
            <Row label="Disposition" value={SCR_DISP[a?.scrDisp] || a?.scrDisp} />
            <Row label="PEP class" value={PEP_CLASS[a?.pepClass] || a?.pepClass} />
          </Section>

          <Section number="D" title="Decision">
            <Row label="Outcome" value={DECISION_LABELS[decision] || decision} />
            <Row label="Rationale" value={decisionRationale} />
          </Section>

          {evalGates.allOk ? (
            <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2.5">
              <CheckCircleIcon className="w-4 h-4 shrink-0" />
              All required form sections are complete.
            </div>
          ) : (
            <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5">
              Some required sections are still incomplete — see badges above.
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-100 flex justify-end shrink-0 bg-slate-50/80 rounded-b-xl">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Close preview
          </Button>
        </div>
      </div>
    </div>
  )
}
